import {
  MessageOutlined,
  DollarCircleOutlined,
  DownloadOutlined,
  EditOutlined,
  LoginOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  StopOutlined,
  ThunderboltOutlined,
  UnlockOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Tag } from "antd";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

/**
 * Действие в журнале.
 *
 * Журнал — самая монотонная таблица админки: шестнадцать видов действий,
 * различимых только текстом, и глазами в ней ищут не «что произошло вообще»,
 * а «где здесь отказы» или «кто трогал настройки». Цвет и иконка семейства
 * дают эту выборку без чтения.
 *
 * Цвет по исходу, а не по объекту: подтверждения зелёные, отказы красные,
 * правки оранжевые — иначе «Вывод подтверждён» и «Вывод отклонён» оказались бы
 * одного цвета, а различать нужно именно их.
 */
const ACTIONS: Record<string, { color: string; icon: ReactNode }> = {
  login: { color: "default", icon: <LoginOutlined aria-hidden /> },
  deposit_approve: { color: "green", icon: <DownloadOutlined aria-hidden /> },
  deposit_reject: { color: "red", icon: <DownloadOutlined aria-hidden /> },
  withdrawal_approve: { color: "blue", icon: <UploadOutlined aria-hidden /> },
  withdrawal_paid: { color: "green", icon: <DollarCircleOutlined aria-hidden /> },
  withdrawal_reject: { color: "red", icon: <UploadOutlined aria-hidden /> },
  balance_adjust: { color: "orange", icon: <EditOutlined aria-hidden /> },
  user_block: { color: "red", icon: <StopOutlined aria-hidden /> },
  user_unblock: { color: "green", icon: <UnlockOutlined aria-hidden /> },
  robot_create: { color: "cyan", icon: <RobotOutlined aria-hidden /> },
  robot_update: { color: "cyan", icon: <RobotOutlined aria-hidden /> },
  robot_reorder: { color: "cyan", icon: <RobotOutlined aria-hidden /> },
  settings_update: { color: "geekblue", icon: <SettingOutlined aria-hidden /> },
  accrual_roll: { color: "magenta", icon: <ThunderboltOutlined aria-hidden /> },
  accrual_manual: { color: "magenta", icon: <ThunderboltOutlined aria-hidden /> },
  admin_create: { color: "purple", icon: <SafetyCertificateOutlined aria-hidden /> },
  admin_update: { color: "purple", icon: <SafetyCertificateOutlined aria-hidden /> },
  // Модерация отзывов (доработки §6.2). Четыре типа, а не один: оператор ищет
  // в журнале «что скрывали», а не «что трогали».
  comment_publish: { color: "green", icon: <MessageOutlined aria-hidden /> },
  comment_hide: { color: "volcano", icon: <MessageOutlined aria-hidden /> },
  comment_delete: { color: "red", icon: <MessageOutlined aria-hidden /> },
  comment_reply: { color: "blue", icon: <MessageOutlined aria-hidden /> },
  comment_persona_create: { color: "purple", icon: <MessageOutlined aria-hidden /> },
};

export function ActionTag({ action }: { action: string }) {
  const { t } = useTranslation();
  // Действие приходит с сервера строкой: бэкенд может завести новое раньше,
  // чем о нём узнает админка, и тогда строка обязана отрисоваться как есть.
  const known = ACTIONS[action];

  return (
    <Tag
      color={known?.color ?? "default"}
      icon={known?.icon}
      style={{ marginInlineEnd: 0, paddingBlock: 1, paddingInline: 8 }}
    >
      {t(`action.${action}`, action)}
    </Tag>
  );
}
